import React, { useRef } from 'react';
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
  useReducedMotion
} from 'framer-motion';
import { FaGithub, FaLinkedin, FaArrowRight, FaArrowDown } from 'react-icons/fa';
import TextReveal from './animations/TextReveal';
import NeuralField from './effects/NeuralField';
import CodeRain from './effects/CodeRain';
import MagneticButton from './effects/MagneticButton';
import Typewriter from './effects/Typewriter';
import GlitchText from './effects/GlitchText';
import TerminalFrame from './effects/TerminalFrame';
import StatRing from './effects/StatRing';
import { profile, stats } from '../data/profile';

/** Tilts its children slightly toward the pointer. */
const MouseParallax = ({ children, sensitivity = 40 }) => {
  const ref = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 50, damping: 20 });
  const springY = useSpring(y, { stiffness: 50, damping: 20 });

  const handleMouseMove = (event) => {
    if (prefersReducedMotion || !ref.current) return;
    const { left, top, width, height } = ref.current.getBoundingClientRect();
    x.set((event.clientX - (left + width / 2)) / sensitivity);
    y.set((event.clientY - (top + height / 2)) / sensitivity);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      style={prefersReducedMotion ? undefined : { x: springX, y: springY }}
      className="h-full w-full"
    >
      {children}
    </motion.div>
  );
};

export default function Hero() {
  const prefersReducedMotion = useReducedMotion();
  const { scrollY } = useScroll();
  const photoY = useTransform(scrollY, [0, 600], [0, prefersReducedMotion ? 0 : 90]);
  const ringY = useTransform(scrollY, [0, 600], [0, prefersReducedMotion ? 0 : -70]);

  return (
    <section
      id="home"
      aria-labelledby="hero-heading"
      className="relative flex min-h-screen items-center overflow-hidden bg-slate-50 pt-28 pb-20
                 transition-colors duration-300 dark:bg-slate-950"
    >
      {/* Ambient background */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-grid mask-fade-b opacity-50 dark:opacity-70" />
        <NeuralField className="mask-fade-b" opacity={0.4} />
        <CodeRain className="mask-fade-b" opacity={0.22} density={16} />
        <motion.div
          className="absolute -right-24 top-0 h-[70vw] max-h-[560px] w-[70vw] max-w-[560px] rounded-full
                     bg-blue-500/[0.08] blur-[120px] dark:bg-emerald-500/10"
          animate={prefersReducedMotion ? undefined : { opacity: [0.35, 0.6, 0.35] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute -left-24 bottom-0 h-[70vw] max-h-[520px] w-[70vw] max-w-[520px] rounded-full
                     bg-indigo-500/[0.08] blur-[120px] dark:bg-cyan-500/10"
          animate={prefersReducedMotion ? undefined : { opacity: [0.5, 0.3, 0.5] }}
          transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-6
                      lg:grid-cols-[1.05fr_0.95fr] lg:gap-20">
        {/* Copy */}
        <motion.div
          className="flex flex-col items-center text-center lg:items-start lg:text-left"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Availability */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="inline-flex items-center gap-2.5 rounded-full border border-slate-200/80 bg-white/80 py-1.5 pl-2.5 pr-4
                       font-mono shadow-sm backdrop-blur-md dark:border-emerald-500/20 dark:bg-emerald-500/[0.06]
                       dark:shadow-none"
          >
            <span className="status-dot" aria-hidden="true" />
            <span className="text-[12px] font-medium tracking-wide text-slate-600 dark:text-emerald-300/90">
              status: online · {profile.availability.toLowerCase()}
            </span>
          </motion.div>

          {/* Heading */}
          <div className="mt-8">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.25 }}
              className="mb-3 font-mono text-sm text-slate-500 dark:text-emerald-500/80"
            >
              <Typewriter text={`~/qasim $ whoami → ${profile.firstName}`} delay={200} speed={22} />
            </motion.p>

            <h1
              id="hero-heading"
              className="text-[2.6rem] font-bold leading-[1.05] tracking-[-0.03em] text-slate-900
                         sm:text-6xl lg:text-[4.25rem] dark:text-white"
            >
              <TextReveal
                text={profile.headline.lead}
                className="justify-center lg:justify-start"
                delay={0.3}
              />
              <motion.span
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="mt-1 block"
              >
                <GlitchText text={profile.headline.accent} className="text-gradient" />
              </motion.span>
            </h1>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg dark:text-slate-400"
          >
            {profile.intro}
          </motion.p>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="mt-9 flex flex-wrap items-center justify-center gap-3 lg:justify-start"
          >
            <MagneticButton
              href="#work"
              className="group inline-flex items-center gap-2 rounded-full bg-slate-900 px-7 py-3.5 text-sm font-semibold
                         text-white shadow-lg shadow-slate-900/20 transition-shadow duration-300
                         hover:shadow-xl hover:shadow-blue-500/25
                         dark:bg-white dark:text-slate-900 dark:shadow-white/10"
            >
              View my work
              <FaArrowRight
                className="text-xs transition-transform duration-300 group-hover:translate-x-1"
                aria-hidden="true"
              />
            </MagneticButton>

            <MagneticButton
              href="#contact"
              strength={0.2}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-7 py-3.5
                         text-sm font-semibold text-slate-800 shadow-sm backdrop-blur-md transition-colors duration-300
                         hover:border-blue-500/50 hover:text-blue-600
                         dark:border-white/10 dark:bg-white/[0.04] dark:text-white dark:shadow-none
                         dark:hover:text-blue-400"
            >
              Get in touch
            </MagneticButton>

            <div className="ml-1 flex items-center gap-1">
              {[
                { href: profile.social.github, Icon: FaGithub, label: 'GitHub' },
                { href: profile.social.linkedin, Icon: FaLinkedin, label: 'LinkedIn' }
              ].map(({ href, Icon, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${label} profile`}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-transparent
                             text-slate-500 transition-all duration-300 hover:-translate-y-0.5
                             hover:border-slate-200 hover:bg-white hover:text-slate-900 dark:text-slate-400
                             dark:hover:border-white/10 dark:hover:bg-transparent dark:hover:text-white"
                >
                  <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
                </a>
              ))}
            </div>
          </motion.div>

          {/* Infographic rings */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.05 }}
            className="mt-12 grid w-full max-w-xl grid-cols-2 gap-4 border-t border-slate-200/80 pt-8
                       sm:grid-cols-4 dark:border-emerald-500/10"
          >
            {stats.map((stat) => (
              <StatRing
                key={stat.label}
                value={stat.value}
                suffix={stat.suffix}
                label={stat.label}
                max={Math.max(10, Math.ceil(stat.value * 1.35))}
                size={96}
                stroke={6}
              />
            ))}
          </motion.div>
        </motion.div>

        {/* Portrait */}
        <motion.div
          className="relative hidden h-[560px] w-full lg:block"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        >
          <MouseParallax>
            <div className="group relative flex h-full w-full items-center justify-center">
              {/* Soft plate behind the photo so the fade doesn’t wash out on light backgrounds */}
              <div
                aria-hidden="true"
                className="absolute z-0 h-[380px] w-[380px] rounded-full bg-gradient-to-b from-slate-200/80
                           to-slate-100/40 blur-2xl dark:from-blue-500/10 dark:to-transparent dark:blur-3xl"
              />

              {/* Orbit rings */}
              <motion.div
                aria-hidden="true"
                className="absolute z-0 h-[520px] w-[520px] rounded-full border border-dashed
                           border-slate-300/80 dark:border-white/10"
                style={{ y: ringY }}
                animate={prefersReducedMotion ? undefined : { rotate: 360 }}
                transition={{ duration: 44, repeat: Infinity, ease: 'linear' }}
              />
              <motion.div
                aria-hidden="true"
                className="absolute z-0 h-[420px] w-[420px] rounded-full border border-blue-400/25
                           dark:border-white/[0.07]"
                style={{ y: photoY }}
                animate={prefersReducedMotion ? undefined : { rotate: -360 }}
                transition={{ duration: 58, repeat: Infinity, ease: 'linear' }}
              >
                <span className="absolute left-1/2 top-0 h-2.5 w-2.5 -translate-x-1/2 rounded-full bg-blue-500 shadow-[0_0_18px_4px_rgba(63,140,255,0.55)]" />
              </motion.div>

              {/* Photo */}
              <motion.div
                className="relative z-20 aspect-square w-[400px]"
                style={{ y: photoY }}
              >
                <div
                  className="h-full w-full"
                  style={{
                    maskImage: 'radial-gradient(circle at 50% 40%, black 42%, transparent 74%)',
                    WebkitMaskImage: 'radial-gradient(circle at 50% 40%, black 42%, transparent 74%)'
                  }}
                >
                  <img
                    src={profile.photo}
                    alt={`${profile.name}, software engineer`}
                    width="400"
                    height="400"
                    fetchpriority="high"
                    className="h-full w-full scale-105 object-cover object-top transition-transform
                               duration-700 ease-smooth group-hover:scale-100"
                  />
                </div>
              </motion.div>

              {/* Terminal card */}
              <motion.div
                aria-hidden="true"
                className="absolute left-0 top-[8%] z-30 w-64"
                animate={prefersReducedMotion ? undefined : { y: [0, -10, 0] }}
                transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
              >
                <TerminalFrame title="shell://qasim">
                  <p>
                    <span className="text-emerald-500">const</span> qasim = {'{'}
                  </p>
                  <p className="pl-3">
                    focus: <span className="text-cyan-500">&apos;products&apos;</span>,
                  </p>
                  <p className="pl-3">
                    shipping: <span className="text-emerald-500">true</span>,
                  </p>
                  <p className="pl-3">
                    mode: <span className="text-amber-500">&apos;hacker_craft&apos;</span>
                  </p>
                  <p>
                    {'}'}
                    <span className="ml-0.5 inline-block animate-caret text-emerald-500">▋</span>
                  </p>
                </TerminalFrame>
              </motion.div>

              {/* Deploy status card */}
              <motion.div
                aria-hidden="true"
                className="absolute bottom-[14%] right-[-4%] z-30"
                animate={prefersReducedMotion ? undefined : { y: [0, 10, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
              >
                <TerminalFrame title="ci · main" className="min-w-[11rem]">
                  <div className="flex items-center gap-2">
                    <span className="status-dot" />
                    <span>
                      <span className="block text-[10px] uppercase tracking-wider text-slate-500">build</span>
                      <span className="block text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                        PASSING
                      </span>
                    </span>
                  </div>
                </TerminalFrame>
              </motion.div>

              {/* Stack chip */}
              <motion.div
                aria-hidden="true"
                className="absolute right-[-6%] top-[38%] z-20 rounded-xl border border-slate-200/90 bg-white/90 px-3 py-2.5
                           shadow-card backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/70 dark:shadow-card-dark"
                animate={prefersReducedMotion ? undefined : { x: [0, 8, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
              >
                <FaGithub className="h-6 w-6 text-slate-900 dark:text-white" />
              </motion.div>
            </div>
          </MouseParallax>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.a
        href="#about"
        aria-label="Scroll to about section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 items-center gap-2 text-[11px]
                   font-medium uppercase tracking-[0.2em] text-slate-400 transition-colors
                   hover:text-blue-500 md:inline-flex"
      >
        Scroll
        <motion.span
          animate={prefersReducedMotion ? undefined : { y: [0, 4, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
        >
          <FaArrowDown className="h-2.5 w-2.5" aria-hidden="true" />
        </motion.span>
      </motion.a>
    </section>
  );
}
